(() => {
  if (customElements.get('onyx-inspection-careers')) return;
  class OnyxInspectionCareers extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => this.initialize(), {once:true, signal:this.abort.signal});
      else this.initialize();
    }
    initialize() {
      this.els = {};
      for (const key of ['launcher','launch','panel','heading','search','role','results','clear','does','role-name','requirements','back','related','dialog','dialog-heading','close','facts','load-status']) this.els[key] = this.querySelector('[data-inspection-'+key+']');
      if (!this.els.launch || !this.els.dialog) return;
      this.selectedRole = 'qc_trainee';
      this.isOpen = false;
      this.addEventListener('click', event => this.click(event), {signal:this.abort.signal});
      this.els.search.addEventListener('input', () => this.populateRoles(), {signal:this.abort.signal});
      this.els.role.addEventListener('change', () => this.choose(this.els.role.value), {signal:this.abort.signal});
      window.addEventListener('hashchange', () => this.historyChanged(), {signal:this.abort.signal});
      window.addEventListener('popstate', () => this.historyChanged(), {signal:this.abort.signal});
      this.els.dialog.addEventListener('close', () => {
        const target = this.focusBeforePanel?.isConnected ? this.focusBeforePanel : this.els.requirements;
        this.focusBeforePanel = null;
        if (this.isOpen) target.focus({preventScroll:true});
      }, {signal:this.abort.signal});
      if (window.location.hash.startsWith('#inspection/')) this.open(true);
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      if (this.els?.dialog.open) this.els.dialog.close();
    }
    el(tag, className, text) {
      const element = document.createElement(tag);
      if (className) element.className = className;
      if (text !== undefined) element.textContent = text;
      return element;
    }
    text(key) { return this.data.ui[key] || key; }
    async load() {
      if (this.data) return;
      if (!this.dataPromise) this.dataPromise = (async () => {
        const url = new URL(this.dataset.contentUrl, window.location.href);
        if (url.origin !== window.location.origin) throw new Error('Invalid content origin');
        const response = await fetch(url.href, {credentials:'same-origin', cache:'no-store'});
        if (!response.ok) throw new Error('Inspection content unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const script = doc.querySelector('[data-inspection-careers-data]');
        if (!script) throw new Error('Inspection content missing');
        const data = JSON.parse(script.textContent);
        if (data.version !== 1 || !Array.isArray(data.roles) || !data.roles.some(role => role.id === 'qc_trainee')) throw new Error('Invalid Inspection content');
        this.data = data;
      })().catch(error => {this.dataPromise = null; throw error;});
      await this.dataPromise;
    }
    role() { return this.data.roles.find(role => role.id === this.selectedRole); }
    matchingRoles(query) {
      const words = query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
      return this.data.roles.filter(role => words.every(word => (role.name+' '+role.keywords).toLocaleLowerCase().includes(word)));
    }
    populateRoles() {
      const query = this.els.search.value;
      const matches = this.matchingRoles(query);
      this.els.role.replaceChildren();
      if (!matches.some(role => role.id === this.selectedRole)) {
        const placeholder = this.el('option','',this.text('choose'));
        placeholder.value = ''; placeholder.disabled = true; placeholder.selected = true;
        this.els.role.append(placeholder);
      }
      for (const group of this.data.groups) {
        const roles = matches.filter(role => role.group === group.id);
        if (!roles.length) continue;
        const optgroup = this.el('optgroup'); optgroup.label = group.name;
        for (const role of roles) {
          const option = this.el('option','',role.name); option.value = role.id;
          optgroup.append(option);
        }
        this.els.role.append(optgroup);
      }
      this.els.role.disabled = matches.length === 0;
      this.els.role.value = matches.some(role => role.id === this.selectedRole) ? this.selectedRole : '';
      this.els.results.textContent = matches.length ? matches.length+' '+this.text(matches.length === 1 ? 'count_one' : 'count') : this.text('no_results');
      this.els.clear.hidden = !query;
    }
    async open(fromHistory = false) {
      this.els.launch.disabled = true;
      this.els.launch.setAttribute('aria-busy','true');
      this.els['load-status'].textContent = this.dataset.loadingLabel;
      try {
        await this.load();
        if (!this.isConnected) return;
        if (fromHistory) this.readHash();
        this.isOpen = true;
        this.els.launcher.hidden = true; this.els.panel.hidden = false;
        this.els.launch.setAttribute('aria-expanded','true');
        this.render();
        if (!fromHistory) this.syncHash();
        this.els['load-status'].textContent = '';
        this.focusPath();
      } catch {this.els['load-status'].textContent = this.dataset.errorLabel;}
      finally {this.els.launch.disabled = false; this.els.launch.removeAttribute('aria-busy');}
    }
    readHash() {
      const id = window.location.hash.slice('#inspection/'.length);
      if (this.data.roles.some(role => role.id === id)) this.selectedRole = id;
    }
    syncHash() {
      const url = new URL(window.location.href); url.hash = '#inspection/'+this.selectedRole;
      if (url.href !== window.location.href) window.history.pushState(null,'',url);
    }
    historyChanged() {
      if (window.location.hash.startsWith('#inspection/')) {
        if (!this.data || !this.isOpen) this.open(true);
        else {
          if (this.els.dialog.open) this.els.dialog.close();
          this.readHash(); this.els.search.value = ''; this.render();
        }
      } else if (this.isOpen) this.close(false);
    }
    focusPath() {
      this.els.heading.focus({preventScroll:true});
      this.els.panel.scrollIntoView({block:'start',behavior:'auto'});
    }
    close(changeHistory = true) {
      this.isOpen = false;
      if (this.els.dialog.open) this.els.dialog.close();
      this.els.panel.hidden = true; this.els.launcher.hidden = false;
      this.els.launch.setAttribute('aria-expanded','false');
      if (changeHistory && window.location.hash.startsWith('#inspection/')) {
        const url = new URL(window.location.href); url.hash = ''; window.history.pushState(null,'',url);
      }
      this.els.launch.focus({preventScroll:true});
    }
    choose(id) {
      if (!this.data.roles.some(role => role.id === id)) return;
      if (this.els.dialog.open) this.els.dialog.close();
      this.selectedRole = id; this.els.search.value = '';
      this.render(); this.syncHash();
    }
    render() {
      const role = this.role();
      this.populateRoles();
      this.els['role-name'].textContent = role.name;
      this.els.does.textContent = role.overview;
      this.els.related.replaceChildren();
      for (const id of role.related) {
        const next = this.data.roles.find(role => role.id === id);
        if (!next) continue;
        const button = this.el('button','ohc-related-button',next.name);
        button.type = 'button'; button.dataset.relatedRole = id;
        this.els.related.append(button);
      }
    }
    credentialElement(credential) {
      const item = this.el('li','ohc-credential');
      item.append(this.el('strong','ohc-credential__name',credential.name), this.el('span','ohc-credential__kind',this.text('kind_'+credential.kind)), this.el('p','',credential.note));
      const links = this.el('div','ohc-credential__links');
      for (const ref of [credential.ref,credential.second_ref]) {
        const source = this.data.sources[ref];
        if (!source?.url.startsWith('https://')) continue;
        const link = this.el('a','',this.text('link_'+source.action));
        link.href = source.url; link.target = '_blank'; link.rel = 'noopener noreferrer';
        link.title = source.name; links.append(link);
      }
      if (links.childElementCount) item.append(links);
      else item.append(this.el('p','ohc-employer-route',this.text('employer_route')));
      return item;
    }
    openRequirements() {
      const role = this.role();
      this.focusBeforePanel = document.activeElement;
      this.els['dialog-heading'].textContent = role.name;
      const sections = [];
      for (const [heading, value] of [['what',role.overview],['industries',role.industries],['study',role.study]]) {
        const section = this.el('section','ohc-fact');
        section.append(this.el('h3','',this.text(heading)),this.el('p','',value));
        sections.push(section);
      }
      const section = this.el('section','ohc-fact');
      section.append(this.el('h3','',this.text('credentials')));
      const list = this.el('ul','ohc-credentials');
      for (const credential of role.credentials) list.append(this.credentialElement(credential));
      section.append(list,this.el('p','ohc-credential-hint',this.text('credential_hint')));
      this.els.facts.replaceChildren(...sections,section);
      this.els.dialog.showModal();
      this.els.dialog.scrollTop = 0;
    }
    click(event) {
      const target = event.target.closest('button');
      if (!target || !this.contains(target)) return;
      if (target.matches('[data-inspection-launch]')) this.open();
      else if (target.matches('[data-inspection-back]')) this.close();
      else if (target.matches('[data-inspection-clear]')) {this.els.search.value = ''; this.populateRoles(); this.els.search.focus();}
      else if (target.matches('[data-inspection-close]')) this.els.dialog.close();
      else if (target.matches('[data-inspection-requirements]')) this.openRequirements();
      else if (target.dataset.relatedRole) this.choose(target.dataset.relatedRole);
    }
  }
  customElements.define('onyx-inspection-careers',OnyxInspectionCareers);
})();

